import { PayloadAction, isDraft, original } from '@reduxjs/toolkit';
import { AddContactsType } from 'app/pages/Users/slice/types';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { CustomError } from 'utils/types/success-error-types';
import { inboxPageSaga } from './saga';
import { reconcileMessages } from './reconcileMessages';
import {
  Client,
  ClientFilterParams,
  DraftEntry,
  FilteredClients,
  Flow,
  GetTemplateLinkType,
  InboxPageState,
  Message,
  MessagesContact,
  MessagesInteractive,
  MessagesLocation,
  MessagesMedia,
  MessagesTemplate,
  MessagesText,
  SendMessagesType,
  SendTemplateVariables,
  Template,
  UpdateClientAttributesAndTagsPayload,
  UpdateClientAttributesAndTagsResponse,
} from './types';
import whatsAppSound from 'files/whatsapp.mp3';
import { nanoid } from 'nanoid';
import { createSocket, socket } from 'utils/context/SocketContext';

interface SendMessageFailEntry {
  message?: { clientWaNumber?: string; refId?: string };
  errorData?: unknown;
}

export interface SendMessagesFailurePayload {
  errorRaw?: { fail?: Record<string, SendMessageFailEntry> };
  fail?: Record<string, SendMessageFailEntry>;
}

export const initialState: InboxPageState = {
  clientList: [],
  filteredClientList: {
    clientList: [],
    selectedFilters: [],
  },
  filterFetch: {
    params: null,
    clients: [],
    nextCursor: null,
    hasMore: false,
    isLoading: false,
  },
  isLoadingClientList: false,
  errorLoadingClientList: null,
  clientListNextCursor: null,
  hasMoreClients: true,
  isLoadingMoreClients: false,

  selectedClient: null,
  isClientClosed: null,

  isLoadingMessages: false,
  errorLoadingMessages: null,
  isLoadingMoreMessages: false,

  isFetchTemplates: false,
  allFetchTemplates: null,

  isFetchFlows: false,
  allFetchFlows: null,

  isAddContacts: false,
  isUpdatingAttributesAndTags: false,
  isSendTemplate: false,

  sentBy: null,
  businessId: -1,

  drafts: new Map(),

  isFetchTemplateLink: false,
  templateLink: [],

  isFromMessageSearch: false,
};

const slice = createSlice({
  name: 'inboxPage',
  initialState,
  reducers: {
    updateSentBy(
      state,
      action: PayloadAction<{
        id: number;
        name: string;
        businessId: number;
      } | null>,
    ) {
      state.sentBy = {
        id: action.payload?.id || 0,
        name: action.payload?.name || '',
      };
      state.businessId = action.payload?.businessId || 0;
    },

    fetchClientListPageStart(
      state,
      _action: PayloadAction<{ limit?: number } | undefined>,
    ) {
      state.isLoadingClientList = true;
      state.clientListNextCursor = null;
      state.hasMoreClients = true;
    },
    fetchClientListPageSuccess(
      state,
      action: PayloadAction<{
        data: Client[];
        nextCursor: string | null;
        usernameMasking: boolean;
      }>,
    ) {
      state.clientList = action.payload.data.map(client => ({
        ...client,
        messages: [],
        isLastMessage: true,
      }));
      state.filteredClientList = {
        clientList: state.clientList,
        selectedFilters: [],
      };
      state.isLoadingClientList = false;
      state.clientListNextCursor = action.payload.nextCursor;
      state.hasMoreClients = Boolean(action.payload.nextCursor);
    },
    fetchClientListPageFailure(state, action: PayloadAction<CustomError>) {
      state.errorLoadingClientList = action.payload;
      state.isLoadingClientList = false;
    },
    loadMoreClientListStart(state) {
      if (!state.hasMoreClients || state.isLoadingMoreClients) return;
      state.isLoadingMoreClients = true;
    },
    loadMoreClientListSuccess(
      state,
      action: PayloadAction<{ data: Client[]; nextCursor: string | null }>,
    ) {
      const existing = new Set(state.clientList.map(c => c.clientWaNumber));
      const incoming = action.payload.data
        .filter(c => !existing.has(c.clientWaNumber))
        .map(client => ({ ...client, messages: [], isLastMessage: true }));
      state.clientList = state.clientList.concat(incoming);
      state.clientListNextCursor = action.payload.nextCursor;
      state.hasMoreClients = Boolean(action.payload.nextCursor);
      state.isLoadingMoreClients = false;
    },
    loadMoreClientListFailure(state, _action: PayloadAction<CustomError>) {
      state.isLoadingMoreClients = false;
    },
    // Active filter changed: reset the backend continuation and (when params
    // are non-null) kick off the first filtered page fetch in the saga.
    filterFetchStart(
      state,
      action: PayloadAction<{ params: ClientFilterParams | null }>,
    ) {
      state.filterFetch.params = action.payload.params;
      state.filterFetch.clients = [];
      state.filterFetch.nextCursor = null;
      state.filterFetch.hasMore = Boolean(action.payload.params);
      state.filterFetch.isLoading = Boolean(action.payload.params);
    },
    filterFetchMoreStart(state) {
      if (
        !state.filterFetch.params ||
        !state.filterFetch.hasMore ||
        state.filterFetch.isLoading
      )
        return;
      state.filterFetch.isLoading = true;
    },
    filterFetchSuccess(
      state,
      action: PayloadAction<{ data: Client[]; nextCursor: string | null }>,
    ) {
      const existing = new Set(
        state.filterFetch.clients.map(c => c.clientWaNumber),
      );
      const incoming = action.payload.data
        .filter(c => !existing.has(c.clientWaNumber))
        .map(client => ({ ...client, messages: [], isLastMessage: true }));
      state.filterFetch.clients = state.filterFetch.clients.concat(incoming);
      state.filterFetch.nextCursor = action.payload.nextCursor;
      state.filterFetch.hasMore = Boolean(action.payload.nextCursor);
      state.filterFetch.isLoading = false;
    },
    filterFetchFailure(state) {
      state.filterFetch.isLoading = false;
      // Stop scroll-driven re-issues of a failing request; a filter toggle
      // (filterFetchStart) re-arms hasMore.
      state.filterFetch.hasMore = false;
    },
    fetchSingleClientStart(
      _state,
      _action: PayloadAction<{ clientWaNumber: string }>,
    ) {},

    fetchMessagesStart(
      state,
      action: PayloadAction<{
        clientWaNumber: string | undefined;
        isFromMessages?: boolean;
        silent?: boolean;
      }>,
    ) {
      if (!action.payload.silent) state.isLoadingMessages = true;
      state.isLoadingMoreMessages = false;
      // A visible refetch tears the window down, so the old cursor answer is
      // stale — stay false until the page lands, or a load fires before there
      // is a cursor to page from. A silent one (the refresh button) leaves the
      // list on screen: clearing it there would flip row 0 back to
      // start-of-history and pop its separator in until the response lands.
      const target = action.payload.clientWaNumber;
      if (
        !action.payload.silent &&
        target &&
        state.selectedClient?.clientWaNumber === target
      )
        state.selectedClient.hasMoreMessages = false;
    },
    fetchMessagesSuccess(
      state,
      action: PayloadAction<{
        messages: Message[];
        canMessage?: boolean;
        conversationExpire?: Date | string | null;
        clientWaNumber: string;
        hasMore?: boolean;
      }>,
    ) {
      state.isLoadingMessages = false;
      const hasMore = action.payload.hasMore ?? false;
      const client = state.clientList?.find(
        client => client.clientWaNumber === action.payload.clientWaNumber,
      );
      // Derive the window from the latest inbound message in the history;
      // fall back to the server's stored window end only when none is loaded.
      const resolvedExpire = resolveConversationExpire(
        action.payload.conversationExpire,
        action.payload.messages,
      );
      if (client) {
        // original() so the O(n) reconcile reads plain objects, not draft
        // proxies — and the preserved identities are the base-state ones.
        // isDraft guard: original() THROWS on non-drafts, and
        // selectedClient.messages can be undefined (Users-page contacts).
        client.messages = reconcileMessages(
          isDraft(client.messages)
            ? original(client.messages)
            : client.messages,
          action.payload.messages,
        );
        client.isLastMessage = false;
        client.hasMoreMessages = hasMore;
        if (resolvedExpire) client.conversationExpire = resolvedExpire;
        state.selectedClient = client;
      } else if (
        state.selectedClient?.clientWaNumber === action.payload.clientWaNumber
      ) {
        state.selectedClient.messages = reconcileMessages(
          isDraft(state.selectedClient.messages)
            ? original(state.selectedClient.messages)
            : state.selectedClient.messages,
          action.payload.messages,
        );
        state.selectedClient.isLastMessage = false;
        state.selectedClient.hasMoreMessages = hasMore;
        if (resolvedExpire)
          state.selectedClient.conversationExpire = resolvedExpire;
      }
      const messageIndex = findLastIndex(
        action.payload.messages,
        msg => msg.status === 'received' && msg?.wamid?.length > 0,
      );
      if (messageIndex !== -1) {
        const Socket = socket ?? createSocket(state.businessId.toString());
        Socket.emit('markReadMessages', {
          businessId: state.businessId,
          wamid: action.payload.messages[messageIndex].wamid,
        });
      }
    },
    fetchMessagesFailure(state, action: PayloadAction<CustomError>) {
      state.isLoadingMessages = false;
      state.errorLoadingMessages = action.payload;
      if (state.selectedClient) state.selectedClient.isLastMessage = false;
    },
    loadMoreMessagesStart(
      state,
      _action: PayloadAction<{ clientWaNumber: string }>,
    ) {
      if (!state.selectedClient?.hasMoreMessages || state.isLoadingMoreMessages)
        return;
      state.isLoadingMoreMessages = true;
    },
    loadMoreMessagesSuccess(
      state,
      action: PayloadAction<{
        messages: Message[];
        clientWaNumber: string;
        hasMore: boolean;
      }>,
    ) {
      // The spinner is cleared either way, but a page that landed after the
      // user switched chats must not overwrite the new chat's cursor flag.
      state.isLoadingMoreMessages = false;
      if (
        state.selectedClient?.clientWaNumber !== action.payload.clientWaNumber
      )
        return;

      // Read from selectedClient, never from the clientList entry: reducers
      // like clearSessionSuccess append to selectedClient.messages only, so the
      // list copy can be a message behind and prepending onto it would drop
      // whatever is missing. original() keeps the loaded rows at their
      // base-state identities so prepending does not remount visible rows.
      const selected = state.selectedClient;
      const loaded =
        (isDraft(selected.messages)
          ? original(selected.messages)
          : selected.messages) ?? [];
      const seen = new Set(loaded.map(message => message.wamid));
      const older = action.payload.messages.filter(
        message => !seen.has(message.wamid),
      );
      if (!older.length) {
        // Exhausted is still worth recording; a stale duplicate page is not.
        if (!action.payload.hasMore) selected.hasMoreMessages = false;
        return;
      }
      selected.hasMoreMessages = action.payload.hasMore;

      const merged = older.concat(loaded);
      selected.messages = merged;
      const listed = state.clientList?.find(
        c => c.clientWaNumber === action.payload.clientWaNumber,
      );
      if (listed) {
        listed.messages = merged;
        listed.hasMoreMessages = action.payload.hasMore;
      }
    },
    loadMoreMessagesFailure(state, _action: PayloadAction<CustomError>) {
      state.isLoadingMoreMessages = false;
    },
    updateClientListOnReceive(
      state,
      action: PayloadAction<{
        message: Message;
        client?: Client;
      }>,
    ) {
      const { message, client: payloadClient } = action.payload;

      if (!payloadClient || !message) {
        return;
      }

      const { clientWaNumber } = payloadClient;
      const clientIndex = findClienIndexByWaNumber(
        state.clientList,
        payloadClient?.clientWaNumber,
      );

      const isSelectedClient =
        state.selectedClient?.clientWaNumber === clientWaNumber;

      // Bot on/off system notes carry a skeleton client ({clientWaNumber,
      // isBotReply}) — never seed a phantom chat row from them, and never
      // reorder the list for them (no real message was exchanged).
      const isBotStatusNote = message.metaData?.isBotOn !== undefined;
      if (isBotStatusNote && clientIndex === -1 && !isSelectedClient) return;

      const existingClient =
        clientIndex > -1 ? state.clientList[clientIndex] : { ...payloadClient };
      const baseClient = isSelectedClient
        ? state.selectedClient
        : existingClient;

      if (!baseClient) return;

      const nonTemplateUpdates = ['conversationExpire', 'isOpen'];
      const allMessageUpdates = [
        'unreadMessages',
        'latestMessage',
        'businessEmployees',
        'optedIn',
        'sessionClearedAt',
      ];

      if (message.type !== 'template') {
        nonTemplateUpdates.forEach(prop => {
          if (
            payloadClient[prop] !== undefined &&
            payloadClient[prop] !== null
          ) {
            baseClient[prop] = payloadClient[prop];
          }
        });
      }

      allMessageUpdates.forEach(prop => {
        if (payloadClient[prop] !== undefined && payloadClient[prop] !== null) {
          baseClient[prop] = payloadClient[prop];
        }
      });

      // A fresh inbound (customer) message re-opens the 24h service window.
      // Derive it from the message timestamp so the composer flips to "active"
      // immediately — independent of whether the socket payload carried a
      // (possibly stale or missing) conversationExpire. Never shrink a window
      // that is already further ahead.
      if (message.status === 'received' && message.timestamp) {
        const reopened = new Date(
          new Date(message.timestamp).getTime() + CONVERSATION_WINDOW_MS,
        );
        const current = baseClient.conversationExpire
          ? new Date(baseClient.conversationExpire)
          : null;
        if (!current || current.getTime() < reopened.getTime()) {
          baseClient.conversationExpire = reopened;
        }
      }

      baseClient.messages = updateMessageList(
        baseClient?.messages ?? [],
        message,
      );

      if (isSelectedClient && state.businessId > 0) {
        baseClient.unreadMessages = 0;
        const Socket = socket ?? createSocket(state.businessId.toString());

        Socket.emit('unreadMessages', {
          businessId: state.businessId,
          clientWaNumber: clientWaNumber,
          unreadMessages: 0,
        });

        if (message?.status === 'received') {
          Socket.emit('markReadMessages', {
            businessId: state.businessId,
            wamid: message.wamid,
          });
        }
        state.selectedClient = baseClient;
      }

      state.clientList = isBotStatusNote
        ? state.clientList.map(c =>
            c.clientWaNumber === clientWaNumber ? baseClient : c,
          )
        : [
            baseClient,
            ...state.clientList.filter(
              c => c.clientWaNumber !== clientWaNumber,
            ),
          ];

      const isCallMsg =
        message.type === 'interactive' &&
        message.interactive?.['type'] === 'call';

      if (message?.status === 'received' && !isCallMsg) whatsappSound();
    },
    updateStatusOnSend(
      state,
      action: PayloadAction<{
        clientWaNumber: string;
        message: Message;
        conversationExpire?: Date;
        latestMessage?: { text: string; type: string; timestamp: Date };
      }>,
    ) {
      const { message, clientWaNumber, conversationExpire, latestMessage } =
        action.payload;

      const updateClientMessages = (client: Client) => {
        if (conversationExpire) client.conversationExpire = conversationExpire;
        if (latestMessage) client.latestMessage = latestMessage;

        // Use the improved updateMessageList function for proper deduplication
        client.messages = updateMessageList(client.messages, message);
      };

      if (state.selectedClient?.clientWaNumber === clientWaNumber) {
        updateClientMessages(state.selectedClient);
      }
      const clientIndex = state.clientList.findIndex(
        client => client.clientWaNumber === clientWaNumber,
      );
      if (
        clientIndex >= 0 &&
        state.selectedClient?.clientWaNumber ===
          state.clientList[clientIndex].clientWaNumber
      ) {
        state.clientList[clientIndex] = state.selectedClient;
      } else if (clientIndex !== -1) {
        const client = state.clientList[clientIndex];
        updateClientMessages(client);
      }
    },
    setSelectedClient(state, action: PayloadAction<Client | null>) {
      if (action.payload) {
        const clientWaNumber = action.payload.clientWaNumber;
        sessionStorage.setItem('selectedClientWaNumber', clientWaNumber);
        const client = state.clientList.find(
          client => client.clientWaNumber === clientWaNumber,
        );
        if (client) {
          client.unreadMessages = 0;
          const Socket = socket ?? createSocket(state.businessId.toString());
          Socket.emit('unreadMessages', {
            businessId: state.businessId,
            clientWaNumber: client.clientWaNumber,
            unreadMessages: 0,
          });
          state.selectedClient = client;
        } else if (
          state.filterFetch.clients.some(
            c => c.clientWaNumber === clientWaNumber,
          ) ||
          action.payload.latestMessage
        ) {
          // Backend-filtered continuation rows are frozen snapshots no live
          // reducer touches, and a chat opened by URL (new chat, group,
          // handoff link) may sit outside the loaded window — promote the
          // client into clientList (sorted by latest message, ghosts at the
          // tail) so it has a row and read receipts, unread badges and socket
          // updates work exactly like loaded clients.
          const promoted = {
            ...action.payload,
            unreadMessages: 0,
            isFilterPromoted: true,
          };
          const ts = promoted.latestMessage?.timestamp
            ? new Date(promoted.latestMessage.timestamp).getTime()
            : 0;
          let insertAt = state.clientList.length;
          for (let i = 0; i < state.clientList.length; i++) {
            const otherRaw = state.clientList[i].latestMessage?.timestamp;
            const otherTs = otherRaw ? new Date(otherRaw).getTime() : 0;
            if (otherTs < ts) {
              insertAt = i;
              break;
            }
          }
          state.clientList.splice(insertAt, 0, promoted);
          state.filterFetch.clients = state.filterFetch.clients.filter(
            c => c.clientWaNumber !== clientWaNumber,
          );
          const Socket = socket ?? createSocket(state.businessId.toString());
          Socket.emit('unreadMessages', {
            businessId: state.businessId,
            clientWaNumber,
            unreadMessages: 0,
          });
          state.selectedClient = state.clientList[insertAt];
        } else {
          state.selectedClient = action.payload;
        }
      } else {
        sessionStorage.removeItem('selectedClientWaNumber');
        state.selectedClient = action.payload;
      }
    },
    setReadUnread(
      state,
      action: PayloadAction<
        {
          clientWaNumber: string;
          unreadMessages: number;
        }[]
      >,
    ) {
      if (action.payload) {
        action.payload.forEach(item => {
          const client = state.clientList.find(
            client => client.clientWaNumber === item.clientWaNumber,
          );
          if (client) {
            client.unreadMessages = item.unreadMessages;
          }
        });
      }
    },

    selectClientByUrl(
      state,
      action: PayloadAction<{ clientWaNumber: string }>,
    ) {
      const client: Client = {
        id: nanoid(),
        username: action.payload.clientWaNumber,
        unreadMessages: 0,
        isOpen: true,
        conversationExpire: new Date(),
        messages: [],
        isLastMessage: false,
        createdAt: new Date(),
        clientWaNumber: action.payload.clientWaNumber,
        countryCode: 91,
        attributes: '{}',
        latestMessage: null,
        businessEmployees: null,
        isBotReply: true,
        isBlocked: false,
      };
      state.selectedClient = client;
    },
    toggleClientStart(
      _state,
      _action: PayloadAction<{ clientWaNumber: string; isOpen: boolean }>,
    ) {},
    toggleClientSuccess(
      state,
      action: PayloadAction<{ clientWaNumber: string; result: boolean }>,
    ) {
      const client = state.clientList.find(
        client => client.clientWaNumber === action.payload.clientWaNumber,
      );

      if (client) {
        client.isOpen = action.payload.result;
        state.selectedClient = client;
      }
    },
    toggleClientFailure(_state, _action: PayloadAction<CustomError>) {},
    toggleBotForClientStart(
      _state,
      _action: PayloadAction<{ clientWaNumber: string; isBotReply: boolean }>,
    ) {},
    toggleBotForClientSuccess(
      state,
      action: PayloadAction<{ clientWaNumber: string; result: boolean }>,
    ) {
      const clientIndex = state.clientList.findIndex(
        client => client.clientWaNumber === action.payload.clientWaNumber,
      );
      let client: Client | null = null;
      if (
        state.selectedClient?.clientWaNumber === action.payload.clientWaNumber
      ) {
        client = state.selectedClient;
      }
      if (client) {
        client.isBotReply = action.payload.result;
        state.selectedClient = client;
        if (clientIndex !== -1) state.clientList[clientIndex] = client;
      } else if (clientIndex !== -1) {
        state.clientList[clientIndex].isBotReply = action.payload.result;
      }
    },
    toggleBotForClientFailure(_state, _action: PayloadAction<CustomError>) {},
    toggleBlockClientStart(
      _state,
      _action: PayloadAction<{ clientWaNumber: string; isBlocked: boolean }>,
    ) {},
    toggleBlockClientSuccess(
      state,
      action: PayloadAction<{ clientWaNumber: string; result: boolean }>,
    ) {
      const clientIndex = state.clientList.findIndex(
        client => client.clientWaNumber === action.payload.clientWaNumber,
      );
      let client: Client | null = null;
      if (
        state.selectedClient?.clientWaNumber === action.payload.clientWaNumber
      ) {
        client = state.selectedClient;
      }
      if (client) {
        client.isBlocked = action.payload.result;
        state.selectedClient = client;
        if (clientIndex !== -1) state.clientList[clientIndex] = client;
      } else if (clientIndex !== -1) {
        state.clientList[clientIndex].isBlocked = action.payload.result;
      }
    },
    toggleBlockClientFailure(_state, _action: PayloadAction<CustomError>) {},
    clearSessionStart(
      _state,
      _action: PayloadAction<{ clientWaNumber: string }>,
    ) {},
    clearSessionSuccess(
      state,
      action: PayloadAction<{
        clientWaNumber: string;
        sessionClearedAt: string;
        message: Message;
      }>,
    ) {
      const { clientWaNumber, sessionClearedAt, message } = action.payload;
      const clientIndex = state.clientList.findIndex(
        client => client.clientWaNumber === clientWaNumber,
      );
      if (state.selectedClient?.clientWaNumber === clientWaNumber) {
        state.selectedClient.sessionClearedAt = new Date(sessionClearedAt);
        if (message) {
          state.selectedClient.messages = updateMessageList(
            state.selectedClient.messages,
            message,
          );
        }
      }
      if (clientIndex !== -1) {
        state.clientList[clientIndex].sessionClearedAt = new Date(
          sessionClearedAt,
        );
      }
    },
    clearSessionFailure(_state, _action: PayloadAction<CustomError>) {},
    getTemplatesStart: {
      reducer: state => {
        state.isFetchTemplates = true;
      },
      prepare: (payload: { refresh?: boolean } = {}) => ({
        payload,
      }),
    },
    getTemplatesSuccess(state, action: PayloadAction<Template[]>) {
      state.allFetchTemplates = action.payload;
      state.isFetchTemplates = false;
    },
    getTemplatesFailure(state, _action: PayloadAction<CustomError>) {
      state.isFetchTemplates = false;
    },
    addOrUpdateTemplate(state, action: PayloadAction<Template>) {
      if (!state.allFetchTemplates) {
        state.allFetchTemplates = [];
      }
      const exists = state.allFetchTemplates.some(
        template => template.id === action.payload.id,
      );
      if (exists) {
        state.allFetchTemplates = state.allFetchTemplates.map(template =>
          template.id === action.payload.id ? action.payload : template,
        );
      } else {
        state.allFetchTemplates = [...state.allFetchTemplates, action.payload];
      }
    },
    getFlowsStart(state) {
      state.isFetchFlows = true;
    },
    getFlowsSuccess(state, action: PayloadAction<Flow[]>) {
      state.allFetchFlows = action.payload;
      state.isFetchFlows = false;
    },
    getFlowsFailure(state, _action: PayloadAction<CustomError>) {
      state.isFetchFlows = false;
    },
    addContactsStart(
      state,
      _action: PayloadAction<AddContactsType[] | unknown>,
    ) {
      state.isAddContacts = true;
    },
    addContactsSuccess(
      state,
      action: PayloadAction<Array<Omit<Client, 'messages'>>>,
    ) {
      state.isAddContacts = false;

      const clientMap = new Map(
        state.clientList.map(client => [client.clientWaNumber, client]),
      );
      action.payload.forEach(contact => {
        const existingClient = clientMap.get(contact.clientWaNumber);

        if (existingClient) {
          existingClient.attributes = contact.attributes;
          existingClient.username = contact.username;
          // Update selectedClient fields without replacing the object (to preserve messages)
          if (
            state.selectedClient?.clientWaNumber ===
            existingClient.clientWaNumber
          ) {
            state.selectedClient.attributes = contact.attributes;
            state.selectedClient.username = contact.username;
          }
        } else {
          state.clientList.push({ ...contact, messages: [] });
        }
      });
    },
    addContactsFailure(state, _action: PayloadAction<CustomError>) {
      state.isAddContacts = false;
    },
    updateClientAttributesAndTagsStart(
      state,
      _action: PayloadAction<UpdateClientAttributesAndTagsPayload[]>,
    ) {
      state.isUpdatingAttributesAndTags = true;
    },
    updateClientAttributesAndTagsSuccess(
      state,
      action: PayloadAction<UpdateClientAttributesAndTagsResponse>,
    ) {
      state.isUpdatingAttributesAndTags = false;
      const { results } = action.payload;

      // Update clients with successful results
      results.forEach(result => {
        if (result.success && result.attributes) {
          const clientIndex = state.clientList.findIndex(
            client => client.clientWaNumber === result.clientWaNumber,
          );
          if (clientIndex !== -1) {
            state.clientList[clientIndex].attributes = result.attributes;
          }
          if (state.selectedClient?.clientWaNumber === result.clientWaNumber) {
            state.selectedClient.attributes = result.attributes;
          }
        }
      });
    },
    updateClientAttributesAndTagsFailure(
      state,
      _action: PayloadAction<CustomError>,
    ) {
      state.isUpdatingAttributesAndTags = false;
    },
    sendMessagesStart(state, action: PayloadAction<SendMessagesType>) {
      state.isSendTemplate = true;
      const clientLookup = new Map<string, Client & { index: number }>(
        state.clientList.map((client, index) => [
          client.clientWaNumber,
          { ...client, index },
        ]),
      );
      const newClientList = [...state.clientList];
      action.payload.forEach(payloadItem => {
        const clientWithIndex = clientLookup.get(payloadItem.clientWaNumber);
        const newMessage = createMessage(
          payloadItem,
          state.sentBy?.name || '',
          clientWithIndex?.messages || [],
          state.allFetchTemplates,
        );
        if (
          state.selectedClient &&
          state.selectedClient.clientWaNumber === payloadItem.clientWaNumber
        ) {
          state.selectedClient.messages.push(newMessage);
        }
        if (clientWithIndex) {
          const { index, ...client } = clientWithIndex;
          client.messages.push(newMessage);
          if (state.selectedClient?.clientWaNumber === client.clientWaNumber) {
            newClientList[index] = state.selectedClient;
          } else if (index >= 0) {
            newClientList[index] = client;
          }
        }
      });
      state.clientList = newClientList;
    },
    sendMessagesFailure(
      state,
      action: PayloadAction<SendMessagesFailurePayload | undefined>,
    ) {
      state.isSendTemplate = false;
      const fail = action.payload?.errorRaw?.fail || action.payload?.fail;
      if (fail && state.selectedClient) {
        const failArray = Object.values(fail);
        const newMessages = [...(state.selectedClient?.messages || [])];
        let clientUpdated = false;
        for (const error of failArray) {
          const message = error?.message;
          if (
            message &&
            state.selectedClient?.clientWaNumber === message?.clientWaNumber
          ) {
            const messageIndex = findLastIndex(
              state.selectedClient.messages,
              m => m.refId === message.refId,
            );
            if (messageIndex !== -1) {
              newMessages[messageIndex] = {
                ...newMessages[messageIndex],
                status: 'failed',
                failureReason: JSON.stringify(error?.errorData || ''),
              };
              clientUpdated = true;
            }
          }
        }
        if (clientUpdated) {
          state.selectedClient = {
            ...state.selectedClient,
            messages: newMessages,
          };
        }
      }
    },
    setDrafts(state, action: PayloadAction<Map<string, DraftEntry>>) {
      state.drafts = action.payload;
    },
    assignClient(
      state,
      action: PayloadAction<{
        employees: { name: string; email: string }[];
        clientWaNumber: string;
      }>,
    ) {
      const { employees, clientWaNumber } = action.payload;
      if (state.selectedClient) {
        state.selectedClient.businessEmployees = employees;
        const clientIndex = state.clientList.findIndex(
          client => client.clientWaNumber === clientWaNumber,
        );
        if (clientIndex !== -1) {
          state.clientList[clientIndex].businessEmployees = employees;
        }
      }
    },
    addTemplateMediaLink(state, action: PayloadAction<GetTemplateLinkType>) {
      const exists = state.templateLink.some(
        template => template.templateId === action.payload.templateId,
      );
      if (exists) {
        state.templateLink = state.templateLink.map(template =>
          template.templateId === action.payload.templateId
            ? action.payload
            : template,
        );
      } else {
        state.templateLink = [...state.templateLink, action.payload];
      }
    },
    bulkAddTemplateMediaLink(
      state,
      action: PayloadAction<
        GetTemplateLinkType & { targetBusinessId?: number }
      >,
    ) {
      // Copy-to-business: the link belongs to another business, not this tab's
      // active one — the saga still POSTs it; just don't cache it locally.
      if (action.payload.targetBusinessId) return;
      const exists = state.templateLink.some(
        template => template.templateId === action.payload.templateId,
      );
      if (exists) {
        state.templateLink = state.templateLink.map(template =>
          template.templateId === action.payload.templateId
            ? action.payload
            : template,
        );
      } else {
        state.templateLink = [...state.templateLink, action.payload];
      }
    },
    getTemplateMediaLinkStart(state) {
      state.isFetchTemplateLink = false;
    },
    getTemplateMediaLinkSuccess(
      state,
      action: PayloadAction<GetTemplateLinkType[]>,
    ) {
      state.templateLink = action.payload;
      state.isFetchTemplateLink = true;
    },
    getTemplateMediaLinkFailure(state, _action: PayloadAction<CustomError>) {
      state.isFetchTemplateLink = true;
    },
    makeFromIsMessageTrue(state) {
      state.isFromMessageSearch = true;
    },
    makeFromIsMessageFalse(state) {
      state.isFromMessageSearch = false;
    },
    updateClientField(state, action: PayloadAction<Client>) {
      const clientIndex = state.clientList.findIndex(
        client => client.clientWaNumber === action.payload.clientWaNumber,
      );
      if (clientIndex !== -1) {
        state.clientList[clientIndex] = {
          ...state.clientList[clientIndex],
          ...action.payload,
        };
        if (
          state.selectedClient?.clientWaNumber === action.payload.clientWaNumber
        ) {
          state.selectedClient = {
            ...state.selectedClient,
            ...action.payload,
          };
        }
      } else if (action.payload.clientWaNumber) {
        state.clientList = [
          { ...createClientObject(action.payload), ...action.payload },
          ...state.clientList,
        ];
      }
    },
    setFilteredClientList(state, action: PayloadAction<FilteredClients>) {
      state.filteredClientList = action.payload;
    },
  },
});

export const { actions: inboxPageActions } = slice;
export const inboxPageReducer = slice.reducer;

export const useInboxPageSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: inboxPageSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useInboxPageSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */

let notificationAudio: HTMLAudioElement | null = null;
export const whatsappSound = () => {
  // Single reused element — a fresh Audio per incoming message re-fetches /
  // re-decodes the clip and piles up GC garbage during bursts.
  if (!notificationAudio) notificationAudio = new Audio(whatsAppSound);
  notificationAudio.currentTime = 0;
  notificationAudio.play()?.catch(error => {
    console.error('Failed to play sound:', error);
  });
};

const findClienIndexByWaNumber = (
  clients: Client[],
  clientWaNumber: string,
) => {
  return clients.findIndex(client => client.clientWaNumber === clientWaNumber);
};

const updateMessageList = (messages: Message[], newMessage: Message) => {
  if (newMessage?.refId) {
    const messageIndex = findLastIndex(
      messages,
      message => message?.refId === newMessage?.refId,
    );
    if (messageIndex >= 0) {
      const context = messages[messageIndex].context || newMessage.context;
      return [
        ...messages.slice(0, messageIndex),
        { ...newMessage, context }, // Use object spread to include context
        ...messages.slice(messageIndex + 1),
      ];
    }
  }
  if (newMessage?.wamid) {
    const messageIndex = findLastIndex(
      messages,
      message => message?.wamid === newMessage?.wamid,
    );
    if (messageIndex >= 0) {
      const context = messages[messageIndex].context || newMessage.context;
      return [
        ...messages.slice(0, messageIndex),
        { ...newMessage, context }, // Use object spread to include context
        ...messages.slice(messageIndex + 1),
      ];
    }
  }

  // If no match found by wamid or refId, append as new message
  return [...messages, newMessage];
};

function findLastIndex<T>(
  array: T[],
  predicate: (value: T, index: number, obj: T[]) => boolean,
): number {
  for (let i = array.length - 1; i >= 0; i--) {
    if (predicate(array[i], i, array)) return i;
  }
  return -1;
}

function processMessageContext(
  message: Message,
  contextId: string | undefined,
  messages?: Message[],
) {
  if (!contextId && !messages) return;
  if (!contextId || !messages) return;

  const contextMessage = messages.find(msg => msg.wamid === contextId);
  if (!contextMessage) return;
  contextMessage.context = null;
  if (contextMessage) message.context = contextMessage;
}

export function createMessage(
  payloadItem: SendMessagesType[number],
  sentByName: string,
  messages: Message[],
  templates?: Template[] | null,
): Message {
  const baseMessage: Message = {
    wamid: nanoid(),
    clientWaNumber: payloadItem.clientWaNumber,
    phoneNumberId: '',
    status: 'waiting',
    timestamp: new Date(),
    reaction: null,
    body: null,
    caption: null,
    name: null,
    size: null,
    type: payloadItem.messageType,
    mimeType: null,
    sha256: null,
    langCode: null,
    awsLink: undefined,
    context: null,
    buttons: null,
    forwarded: false,
    frequently_forwarded: false,
    interactive: {},
    sentByName: sentByName,
    advertisementId: null,
    dimensions: null,
    failureReason: null,
    refId: payloadItem.refId,
    ...(payloadItem.isPrivate && { metaData: { isPrivate: true } }),
  };

  switch (payloadItem.messageType) {
    case 'text':
      payloadItem = payloadItem as MessagesText;
      baseMessage.body = payloadItem.message;
      baseMessage.type = payloadItem.messageType;
      baseMessage.refId = payloadItem.refId;
      processMessageContext(baseMessage, payloadItem.contextId, messages); // Note: selectedClient is not available here
      break;
    case 'media':
      payloadItem = payloadItem as MessagesMedia;
      baseMessage.name = payloadItem.name;
      baseMessage.size = payloadItem.size;
      baseMessage.mimeType = payloadItem.mimeType;
      baseMessage.awsLink = payloadItem.url;
      baseMessage.caption = payloadItem.caption ?? null;
      baseMessage.refId = payloadItem.refId;
      baseMessage.type = payloadItem.messageType;
      processMessageContext(baseMessage, payloadItem.contextId, messages);
      break;
    case 'template': {
      payloadItem = payloadItem as MessagesTemplate;
      baseMessage.langCode = payloadItem.languageCode;
      baseMessage.name = payloadItem.templateName;
      baseMessage.templateName = payloadItem.templateName;
      baseMessage.type = payloadItem.messageType;
      baseMessage.refId = payloadItem.refId;
      // Process template variables and content
      payloadItem.variables.forEach(item => {
        if (item.type === 'header') {
          item.parameters.forEach(param => {
            if (['image', 'video', 'document'].includes(param.type)) {
              baseMessage.awsLink = param[param.type].link;
              baseMessage.mimeType = param.type;
            } else if (
              ['text'].includes(param.type) &&
              'templateHeader' in payloadItem
            ) {
              baseMessage.interactive = {
                header: {
                  type: 'text',
                  text: payloadItem.templateHeader?.replace(/{{\d+}}/g, m => {
                    const param =
                      item.parameters[parseInt(m.replace(/\D/g, '')) - 1];
                    return 'text' in param ? param.text : '';
                  }),
                },
              };
            }
          });
        } else if (item.type === 'body' && 'templateContent' in payloadItem) {
          baseMessage.body = payloadItem.templateContent.replace(
            /{{\d+}}/g,
            m => {
              const param = item.parameters[parseInt(m.replace(/\D/g, '')) - 1];
              return 'text' in param ? param.text : '';
            },
          );
        } else if (item.type === 'carousel') {
          // Find template for carousel buttons
          let templateCarouselCards: TemplateCarouselCard[] | undefined;
          if (templates) {
            const templatePayload = payloadItem as MessagesTemplate;
            const template = templates.find(
              t =>
                t.name === templatePayload.templateName &&
                t.language === templatePayload.languageCode,
            );
            if (template) {
              const carouselComponent = template.components?.find(
                c => c.type === 'CAROUSEL',
              );
              // Map cards to expected type structure
              templateCarouselCards = carouselComponent?.cards?.map(card => ({
                components: card.components.map(comp => ({
                  type: comp.type,
                  format: comp.format,
                  text: comp.text,
                  example: comp.example,
                  add_security_recommendation: comp.add_security_recommendation,
                  code_expiration_minutes: comp.code_expiration_minutes,
                  buttons: comp.buttons?.map(btn => ({
                    type: btn.type,
                    text: btn.text || '',
                    url: 'url' in btn ? btn.url : undefined,
                    example: 'example' in btn ? btn.example : undefined,
                    phone_number:
                      'phone_number' in btn ? btn.phone_number : undefined,
                    flow_id: 'flow_id' in btn ? btn.flow_id : undefined,
                    flow_action:
                      'flow_action' in btn ? btn.flow_action : undefined,
                    navigate_screen:
                      'navigate_screen' in btn
                        ? btn.navigate_screen
                        : undefined,
                  })),
                })),
              }));
            }
          }
          baseMessage.interactive = {
            type: 'carousel',
            cards: (item.cards || [])
              .sort(
                (a, b) =>
                  (a.card_index ?? Infinity) - (b.card_index ?? Infinity),
              )
              .map((card, index) =>
                processCarouselCard(card, templateCarouselCards?.[index]),
              ),
          };
        }
      });
      if (!payloadItem.variables.find(component => component.type === 'body')) {
        baseMessage.body = payloadItem.templateContent;
      }
      // Find template and add footer/buttons to interactive (for non-carousel templates)
      const interactiveType = (baseMessage.interactive as { type?: string })
        ?.type;
      if (templates && interactiveType !== 'carousel') {
        const templatePayload = payloadItem as MessagesTemplate;
        const template = templates.find(
          t =>
            t.name === templatePayload.templateName &&
            t.language === templatePayload.languageCode,
        );
        if (template) {
          // Header TEXT without variables (e.g. "Welcome!" but not "Hello {{1}}")
          const headerComponent = template.components?.find(
            c =>
              c.type === 'HEADER' &&
              c.format === 'TEXT' &&
              c.text &&
              !/{{\d+}}/.test(c.text),
          );
          const footerComponent = template.components?.find(
            c => c.type === 'FOOTER',
          );
          const buttonsComponent = template.components?.find(
            c => c.type === 'BUTTONS',
          );
          const callPermissionComponent = template.components?.find(
            c =>
              c.type === 'CALL_PERMISSION_REQUEST' ||
              c.type === 'call_permission_request',
          );
          if (
            headerComponent ||
            footerComponent ||
            buttonsComponent ||
            callPermissionComponent
          ) {
            // Get button variables from payload for URL replacement
            const buttonVariables = payloadItem.variables
              .filter(v => v.type === 'button')
              .map(v => v as ButtonVariable);
            // Don't overwrite header if it was already set (e.g. with variables)
            const existingHeader = (
              baseMessage.interactive as { header?: unknown }
            )?.header;
            baseMessage.interactive = {
              ...baseMessage.interactive,
              ...(headerComponent &&
                !existingHeader && {
                  header: { type: 'text', text: headerComponent.text || '' },
                }),
              ...(footerComponent && {
                footer: { type: 'text', text: footerComponent.text || '' },
              }),
              ...(buttonsComponent?.buttons && {
                buttons: processTemplateButtons(
                  buttonsComponent.buttons as TemplateButton[],
                  buttonVariables,
                ),
              }),
              ...(callPermissionComponent && {
                call_permission_request: { type: 'call_permission_request' },
              }),
            };
          }
        }
      }
      break;
    }
    case 'contacts':
      payloadItem = payloadItem as MessagesContact;
      baseMessage.refId = payloadItem.refId;
      baseMessage.type = payloadItem.messageType;
      baseMessage.body = 'Send contacts type message';
      baseMessage.type = 'interactive';
      baseMessage.interactive = {
        contacts: payloadItem.contacts,
        type: 'contacts',
      };
      processMessageContext(baseMessage, payloadItem.contextId, messages);
      break;
    case 'location':
      payloadItem = payloadItem as MessagesLocation;
      baseMessage.refId = payloadItem.refId;
      baseMessage.type = payloadItem.messageType;
      baseMessage.body = 'Send location type message';
      baseMessage.type = 'interactive';
      baseMessage.interactive = {
        location: payloadItem.location,
        type: 'location',
      };
      processMessageContext(baseMessage, payloadItem.contextId, messages);
      break;
    case 'interactive':
      payloadItem = payloadItem as MessagesInteractive;
      baseMessage.refId = payloadItem.refId;
      baseMessage.type = payloadItem.messageType;
      baseMessage.body = payloadItem.interactive.body?.text || '';
      baseMessage.interactive = payloadItem.interactive;
      if (payloadItem.interactive.header?.type !== 'text')
        baseMessage.awsLink =
          payloadItem.interactive.header?.[payloadItem.interactive.header.type]
            ?.link ?? undefined;
      break;
  }

  return baseMessage;
}

const CONVERSATION_WINDOW_MS = 24 * 60 * 60 * 1000;

const findLastReceivedMessageTimestamp = (messages: Message[]): Date | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].status === 'received') {
      return messages[i].timestamp;
    }
  }
  return null;
};

/**
 * Resolve the 24h WhatsApp service-window end for a chat.
 *
 * The latest *inbound* (received) message in the loaded history is the ground
 * truth — the window opens from when the customer last messaged. The contact's
 * stored `conversationExpire` is a derived field that can lag or stick on an
 * older message (out-of-order / redelivered webhooks, or the backend's
 * advance-only guard), so it is only a fallback for when no inbound message is
 * present in the loaded set.
 */
const resolveConversationExpire = (
  serverExpire: Date | string | null | undefined,
  messages: Message[],
): Date | undefined => {
  const lastReceivedTimestamp = findLastReceivedMessageTimestamp(messages);
  if (lastReceivedTimestamp) {
    return new Date(
      new Date(lastReceivedTimestamp).getTime() + CONVERSATION_WINDOW_MS,
    );
  }
  return serverExpire ? new Date(serverExpire) : undefined;
};

// Shared types for template button processing
type TemplateButton = {
  type: string;
  text: string;
  url?: string;
  example?: string[];
  phone_number?: string;
};

type ButtonVariable = {
  type: 'button';
  index?: number;
  sub_type?: string;
  parameters?: Array<{ type: string; text?: string }>;
};

type TemplateCardComponent = {
  type: string;
  buttons?: TemplateButton[];
  [key: string]: unknown;
};

type TemplateCarouselCard = {
  components?: TemplateCardComponent[];
};

/**
 * Process template buttons with URL variable replacement
 * Shared between carousel and non-carousel templates
 */
const processTemplateButtons = (
  templateButtons: TemplateButton[],
  buttonVariables: ButtonVariable[],
): Record<string, unknown>[] => {
  return templateButtons.map((button, index) => {
    // Find matching variable by index
    const matchingVariable = buttonVariables.find(v => v.index === index);

    const processedButton: Record<string, unknown> = {
      type: button.type,
      text: button.text,
    };

    if (button.url) {
      // Replace {{1}} with parameter text if variable exists
      let processedUrl = button.url;
      if (matchingVariable?.parameters?.[0]?.text) {
        processedUrl = button.url.replace(
          /{{\d+}}/g,
          matchingVariable.parameters[0].text,
        );
      }
      processedButton.url = processedUrl;
      if (button.example) {
        processedButton.example = button.example;
      }
      if (matchingVariable?.parameters) {
        processedButton.parameters = matchingVariable.parameters;
      }
    }

    if (button.phone_number) {
      processedButton.phone_number = button.phone_number;
    }

    return processedButton;
  });
};

const processCarouselCard = (
  card: Extract<SendTemplateVariables, { type: 'carousel' }>['cards'][0],
  templateCard?: TemplateCarouselCard,
) => {
  const createHeaderContent = (
    parameter: Extract<
      SendTemplateVariables,
      { type: 'carousel' }
    >['cards'][0]['variables']['0']['parameters'][0],
  ) => {
    const { type, ...content } = parameter as Record<string, unknown>;
    const contentValue = content[type as string];
    const result: Record<string, unknown> = { type };
    if (contentValue && typeof contentValue === 'object') {
      result[type as string] = contentValue;
    }
    return result;
  };
  const createBodyContent = (
    cardContent: string = '',
    parameters: Extract<
      SendTemplateVariables,
      { type: 'carousel' }
    >['cards'][0]['variables']['0']['parameters'],
  ) => ({
    type: 'text',
    text: cardContent
      .replace(/{{\d+}}/g, m => {
        const param = parameters[parseInt(m.replace(/\D/g, '')) - 1];
        return 'text' in param ? param.text : '';
      })
      .replace(/(?: \u200B|\u200B )+/g, ''),
  });
  const processedCard: Record<string, unknown> = {};
  card.variables.forEach(component => {
    if (component.type === 'header' && component.parameters[0]) {
      processedCard.header = createHeaderContent(component.parameters[0]);
    } else if (component.type === 'body') {
      processedCard.body = createBodyContent(
        card.cardContent,
        component.parameters,
      );
    }
  });
  // Ensure there's always a body, even if no body component was found
  if (!processedCard.body) {
    processedCard.body = { text: card.cardContent ?? '', type: 'text' };
  }

  // Process buttons from template card using shared function
  if (templateCard?.components) {
    const buttonsComponent = templateCard.components.find(
      c => c.type === 'BUTTONS',
    );
    if (buttonsComponent?.buttons) {
      const buttonVariables = card.variables
        .filter(v => v.type === 'button')
        .map(v => v as ButtonVariable);
      processedCard.buttons = processTemplateButtons(
        buttonsComponent.buttons,
        buttonVariables,
      );
    }
  }

  return processedCard;
};

export const createClientObject = ({ clientWaNumber }: Partial<Client>) => {
  return {
    id: nanoid(),
    username: clientWaNumber || '',
    unreadMessages: 0,
    isOpen: true,
    conversationExpire: new Date(),
    messages: [],
    isLastMessage: false,
    createdAt: new Date(),
    clientWaNumber: clientWaNumber || '',
    countryCode: 91,
    attributes: '{}',
    latestMessage: null,
    businessEmployees: null,
    isBotReply: true,
  } as Client;
};
